import { useActiveProject } from "@/features/project/ActiveProject";
import {
  type CustomerConsent as CustomerConsentType,
  createConsents,
  updateConsents,
  useConsent,
} from "@repo/api";
import { Button, ListView, ListViewItem } from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import { CustomerConsent, useCustomer } from "@repo/ui";
import { getConsentValue, localizeDate } from "@repo/ui";
import { ModalSheet, useSettings, useToast } from "@repo/ui";
import { getDebugMessage } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import { type ReactElement, useState } from "react";

import { ConsentStatus } from "./ConsentStatus";

type ConsentSettingsStep = "OVERVIEW" | "FOLLOWUP" | "PROMOTIONAL";

interface ConsentSettingsModalProps {
  isOpen: boolean;
  closeModal: () => void;
}

export const ConsentSettingModal = ({
  isOpen,
  closeModal,
}: ConsentSettingsModalProps) => {
  const { opportunity } = useActiveProject();
  const { updateToast } = useToast();
  const { dateFnsLocale } = useTranslation();
  const { consent_requirements } = useSettings();
  const { customer } = useCustomer(opportunity?.id);
  const { data: consentData } = useConsent(opportunity?.partyId, true);

  const [step, setStep] = useState<ConsentSettingsStep>("OVERVIEW");
  const [manualConsents, setManualConsents] = useState<
    CustomerConsentType[] | undefined
  >();

  // TODO: is reason code still used?
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [reason, setReason] = useState<string | undefined>();

  const followupConsent = getConsentValue(
    "FOLLOWUP_CONSENT",
    consentData?.customerConsents,
    opportunity?.hfb,
    opportunity?.id
  );

  const promotionalConsent = getConsentValue(
    "PROMOTIONAL_CONSENT",
    consentData?.customerConsents,
    opportunity?.hfb,
    opportunity?.id
  );

  // update manualConsent
  const updateManualConsent = () => {
    if (opportunity && customer) {
      const existingConsents = manualConsents?.filter(
        (consent) => consent.consentId
      );
      const newConsents = manualConsents?.filter(
        (consent) => !consent.consentId
      );

      const promises: Promise<unknown>[] = [];

      if (existingConsents?.length) {
        promises.push(updateConsents(customer.partyId, existingConsents));
      }
      if (newConsents?.length) {
        promises.push(
          createConsents(customer, newConsents, opportunity.hfb, opportunity.id)
        );
      }

      return Promise.all(promises)
        .then(() => {
          updateToast({
            id: opportunity.id,
            title: t("prompt.project_saved"),
          });
        })
        .catch((e) => {
          updateToast({
            id: opportunity.id,
            title: t("prompt.project_saved_failed"),
            message: getDebugMessage(e),
            type: "error",
          });
        })
        .finally(() => closeModal());
    }
  };

  const view: Record<
    ConsentSettingsStep,
    { title: string; view: ReactElement }
  > = {
    OVERVIEW: {
      title: t("label.consent_settings"),
      view: (
        <ListView id="consent-settings">
          <ListViewItem
            control="navigational"
            title={t("label.consent_for_follow_up")}
            description={
              followupConsent?.updatedAt
                ? `${t("label.updated_at")} ${localizeDate(followupConsent?.updatedAt, dateFnsLocale)}`
                : undefined
            }
            addOn={<ConsentStatus consentType="FOLLOWUP_CONSENT" />}
            onClick={() => setStep("FOLLOWUP")}
          />
          {consent_requirements?.promotional_consent && (
            <ListViewItem
              control="navigational"
              title={t("label.consent_for_promotions")}
              description={
                promotionalConsent?.updatedAt
                  ? `${t("label.updated_at")} ${localizeDate(promotionalConsent?.updatedAt, dateFnsLocale)}`
                  : undefined
              }
              addOn={<ConsentStatus consentType="PROMOTIONAL_CONSENT" />}
              onClick={() => setStep("PROMOTIONAL")}
            />
          )}
        </ListView>
      ),
    },
    FOLLOWUP: {
      title: t("label.consent_for_follow_up"),
      view: (
        <CustomerConsent
          opportunityId={opportunity?.id}
          consentTypes={["FOLLOWUP_CONSENT"]}
          customer={customer}
          isFromCustomerSettings={true}
          departments={opportunity?.hfb}
          manualConsents={manualConsents}
          setManualConsents={setManualConsents}
          setReason={setReason}
        />
      ),
    },
    PROMOTIONAL: {
      title: t("label.consent_for_promotions"),
      view: (
        <CustomerConsent
          opportunityId={opportunity?.id}
          consentTypes={["PROMOTIONAL_CONSENT"]}
          isFromCustomerSettings={true}
          customer={customer}
          departments={opportunity?.hfb}
          manualConsents={manualConsents}
          setManualConsents={setManualConsents}
        />
      ),
    },
  };

  return (
    <ModalSheet
      title={view[step].title}
      visible={isOpen}
      onClose={closeModal}
      onBack={step === "OVERVIEW" ? undefined : () => setStep("OVERVIEW")}
      footer={
        <Button
          type="primary"
          text={isEmpty(manualConsents) ? t("action.close") : t("action.save")}
          onClick={() => {
            if (step !== "OVERVIEW" && !isEmpty(manualConsents)) {
              updateManualConsent();
            } else {
              closeModal();
            }
          }}
        />
      }
    >
      {view[step].view}
    </ModalSheet>
  );
};
